import { useTranslation } from "react-i18next";
import { Checkbox, Form } from "semantic-ui-react";

import { usePermanentRealTimeUser } from "hooks";
import { createFormHelper } from "lib/Form";
import { APPOINTMENT_NOTE_TYPES, STATUS_IDENTIFIERS } from "models";
import { enumToDropdownOptions, isAdmin } from "utils/common";

import type { LocationAdminFormPayload } from "modules/DealerLocationDetails";

const FormHelper = createFormHelper<LocationAdminFormPayload>();

const NOTE_TYPES = [
  { value: APPOINTMENT_NOTE_TYPES.RecurringCar, labelKey: "v8_recurring_car", labelFallback: "Recurring Car" },
  { value: APPOINTMENT_NOTE_TYPES.BackOrder, labelKey: "v8_backorder", labelFallback: "Backorder" },
  { value: APPOINTMENT_NOTE_TYPES.Info, labelKey: "v8_info", labelFallback: "Info" },
  { value: APPOINTMENT_NOTE_TYPES.Attachment, labelKey: "v8_attachment", labelFallback: "Attachment" },
  { value: APPOINTMENT_NOTE_TYPES.Bill, labelKey: "v8_bill_note", labelFallback: "Bill note" },
  { value: APPOINTMENT_NOTE_TYPES.TempDriver, labelKey: "v8_temporary_driver_note", labelFallback: "Temporary driver note" },
  { value: APPOINTMENT_NOTE_TYPES.ParkingLocation, labelKey: "v8_parking", labelFallback: "Parking" },
  { value: APPOINTMENT_NOTE_TYPES.MakeAnOffer, labelKey: "v8_make_an_offer", labelFallback: "Make an offer" },
  { value: APPOINTMENT_NOTE_TYPES.NoDrive, labelKey: "v8_no_drive_note", labelFallback: "No drive note" },
  { value: APPOINTMENT_NOTE_TYPES.XentryAttachment, labelKey: "v8_xentry_attachment", labelFallback: "Xentry attachment" }
];

export const AppointmentSettings = () => {
  const { t } = useTranslation();

  const { user } = usePermanentRealTimeUser();
  const { watch, setValue } = FormHelper.useFormContext();
  const enabledNoteTypes = watch("enabled_appointment_note_type_ids");

  const isAdminUser = Boolean(user && isAdmin(user.role_id));
  const statusOptions = enumToDropdownOptions({ enumObj: STATUS_IDENTIFIERS, t }).filter(option => option.value < STATUS_IDENTIFIERS.CustomerAnsweredStatus);

  const toggleNoteType = (value: APPOINTMENT_NOTE_TYPES, checked?: boolean) => {
    const noteTypes = enabledNoteTypes ?? [];
    setValue("enabled_appointment_note_type_ids", checked ? [...noteTypes, value] : noteTypes.filter(noteType => noteType !== value));
  };

  return (
    <div className="dealer-location-columns">
      <div className="dealer-location-column">
        <div className="dealer-location-subheading">{t("v8_unpin_appointments", "Unpin appointments")}</div>
        <Form.Field>
          <label>{t("v8_automatically_unpin_appointments_on_selected_statuses", "Automatically unpin appointments on selected statuses")}</label>
          <FormHelper.Field.Dropdown name="auto_unpin_appointment_statuses" options={statusOptions} dropdownProps={{ multiple: true, search: true, clearable: true }} />
        </Form.Field>
        <Form.Field>
          <label>{t("v8_unpin_appointments_older_than_days", "Unpin appointments older than [x] days")}</label>
          <FormHelper.Field.Input name="unpin_past_appointments_older_than" inputProps={{ type: "number", min: 0 }} />
        </Form.Field>
        <Form.Field>
          <label>{t("v8_unpin_appointments_further_than_days", "Unpin appointments further than [x] days")}</label>
          <FormHelper.Field.Input name="unpin_future_appointments_further_than" inputProps={{ type: "number", min: 0 }} />
        </Form.Field>
        <Form.Field>
          <FormHelper.Field.Checkbox name="unpin_appointments_on_is_billed" label={t("v8_unpin_appointment_on_is_billed", "Unpin Appointment On Is Billed")} />
        </Form.Field>
        <Form.Field>
          <FormHelper.Field.Checkbox
            name="unpin_appointments_on_car_out_of_shop"
            label={t("v8_unpin_appointment_on_car_out_of_shop", "Unpin Appointment On Car Out of Shop")}
          />
        </Form.Field>
      </div>

      <div className="dealer-location-column">
        <div className="dealer-location-subheading">{t("v8_additional_appointment_note_types", "Additional Appointment Note Types")}</div>
        {NOTE_TYPES.map(noteType => (
          <Form.Field key={noteType.value}>
            <Checkbox
              label={t(noteType.labelKey, noteType.labelFallback)}
              checked={enabledNoteTypes?.includes(noteType.value) ?? false}
              onChange={(_, { checked }) => toggleNoteType(noteType.value, checked)}
            />
          </Form.Field>
        ))}

        <div className="dealer-location-subheading">{t("v8_notes", "Notes")}</div>
        <Form.Field>
          <FormHelper.Field.Checkbox name="is_car_note_enabled" label={t("v8_car_note", "Car note")} />
        </Form.Field>
        <Form.Field>
          <FormHelper.Field.Checkbox name="is_customer_note_enabled" label={t("v8_customer_note", "Customer note")} />
        </Form.Field>
      </div>

      <div className="dealer-location-column">
        {isAdminUser && (
          <>
            <div className="dealer-location-subheading">{t("v8_new_appointment_settings", "New appointment settings")}</div>
            <Form.Field>
              <FormHelper.Field.Checkbox name="first_name_optional" label={t("v8_first_name_optional", "First name optional")} />
            </Form.Field>

            <div className="dealer-location-subheading">{t("v8_pin_appointments", "Pin appointments")}</div>
            <Form.Field>
              <FormHelper.Field.Checkbox name="automatically_pin_appointments" label={t("v8_automatically_pin_appointments", "Automatically pin appointments")} />
            </Form.Field>
            <Form.Field>
              <FormHelper.Field.Checkbox
                name="automatically_pin_car_in_shop"
                label={t("v8_automatically_pin_car_in_shop_appointments", "Automatically pin Car in shop appointments")}
              />
            </Form.Field>
            <Form.Field>
              <FormHelper.Field.Checkbox name="is_manually_pinning_appointments_enabled" label={t("v8_allow_manual_pinning_red_pin", "Allow manual pinning (Red pin)")} />
            </Form.Field>
            <Form.Field>
              <FormHelper.Field.Checkbox
                name="is_automatically_pinned_appointments_readonly"
                label={t("v8_set_automatic_pin_as_read_only_black_pin", "Set automatic pin as read-only (Black pin)")}
              />
            </Form.Field>

            <div className="dealer-location-subheading">{t("v8_recurring_car", "Recurring car")}</div>
            <Form.Field>
              <label>{t("v8_recurring_duration_weeks", "Recurring duration (Weeks)")}</label>
              <FormHelper.Field.Input name="recurring_car_duration" inputProps={{ type: "number", min: 0 }} />
            </Form.Field>
          </>
        )}
      </div>
    </div>
  );
};
